import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Badge,
  Modal,
  Table,
} from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import {
  FaExchangeAlt,
  FaSearch,
  FaCheck,
  FaHistory,
} from "react-icons/fa";
import pythonApiClient from "../../../services/api/pythonBaseApi";
import { useZonas } from "../../../services/hooks/useZonas";
import { useModulos } from "../../../services/hooks/useModulos";
import { useHabitaciones } from "../../../services/hooks/useHabitaciones";
import "react-toastify/dist/ReactToastify.css";

const headersProyecto = (proyectoId) => ({
  "X-Proyecto-ID": proyectoId,
});

const CambioHabitacion = () => {
  const selectedProject = JSON.parse(
    localStorage.getItem("selectedProject") || "null"
  );
  const proyectoId = selectedProject?.id;

  const { zonas, fetchZonas } = useZonas();
  const { modulos, fetchModulosByZona, fetchModulos } = useModulos();
  const { habitaciones, fetchHabitacionesByModulo } = useHabitaciones();

  const [dni, setDni] = useState("");
  const [origen, setOrigen] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingBuscar, setLoadingBuscar] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingEjecutar, setLoadingEjecutar] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [observaciones, setObservaciones] = useState("");

  const [zonaId, setZonaId] = useState("");
  const [moduloId, setModuloId] = useState("");
  const [habitacionId, setHabitacionId] = useState("");
  const [camaDestinoId, setCamaDestinoId] = useState("");
  const [camasLibres, setCamasLibres] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);

  useEffect(() => {
    if (proyectoId) {
      fetchZonas(proyectoId);
      cargarHistorial();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proyectoId]);

  const cargarHistorial = async () => {
    if (!proyectoId) return;
    setLoadingHist(true);
    try {
      const res = await pythonApiClient.get(
        "/procesos/hoteleria/cambio-habitacion/historial",
        {
          params: { limit: 30 },
          headers: headersProyecto(proyectoId),
        }
      );
      setHistorial(res.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingHist(false);
    }
  };

  const buscar = async () => {
    if (!proyectoId) {
      toast.error("Seleccione un proyecto");
      return;
    }
    if (!dni.trim()) {
      toast.error("Ingrese el DNI");
      return;
    }
    setLoadingBuscar(true);
    setOrigen(null);
    setPreview(null);
    setCamaDestinoId("");
    try {
      const res = await pythonApiClient.get(
        "/procesos/hoteleria/cambio-habitacion/buscar",
        {
          params: { dni: dni.trim() },
          headers: headersProyecto(proyectoId),
        }
      );
      setOrigen(res.data.origen);
      toast.success("Ocupación encontrada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se encontró ocupación activa");
    } finally {
      setLoadingBuscar(false);
    }
  };

  const onZonaChange = async (id) => {
    setZonaId(id);
    setModuloId("");
    setHabitacionId("");
    setCamaDestinoId("");
    setCamasLibres([]);
    setPreview(null);
    if (id) {
      if (fetchModulosByZona) {
        await fetchModulosByZona(parseInt(id, 10));
      } else {
        await fetchModulos(parseInt(id, 10));
      }
    }
  };

  const onModuloChange = async (id) => {
    setModuloId(id);
    setHabitacionId("");
    setCamaDestinoId("");
    setCamasLibres([]);
    setPreview(null);
    if (id) {
      await fetchHabitacionesByModulo(parseInt(id, 10));
    }
  };

  const onHabitacionChange = async (id) => {
    setHabitacionId(id);
    setCamaDestinoId("");
    setPreview(null);
    if (!id || !proyectoId) {
      setCamasLibres([]);
      return;
    }
    try {
      const res = await pythonApiClient.get(
        "/procesos/hoteleria/cambio-habitacion/camas-libres",
        {
          params: { habitacion_id: id },
          headers: headersProyecto(proyectoId),
        }
      );
      setCamasLibres(res.data?.data || []);
    } catch (err) {
      setCamasLibres([]);
      toast.error(err.response?.data?.detail || "Error al cargar camas libres");
    }
  };

  const hacerPreview = async () => {
    if (!origen?.asignacion_id || !camaDestinoId) {
      toast.error("Seleccione cama destino");
      return;
    }
    setLoadingPreview(true);
    try {
      const res = await pythonApiClient.post(
        "/procesos/hoteleria/cambio-habitacion/preview",
        {
          asignacion_id: origen.asignacion_id,
          cama_destino_id: parseInt(camaDestinoId, 10),
        },
        { headers: headersProyecto(proyectoId) }
      );
      setPreview(res.data);
      setShowConfirm(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo validar el cambio");
    } finally {
      setLoadingPreview(false);
    }
  };

  const confirmar = async () => {
    if (!origen?.asignacion_id || !camaDestinoId) return;
    setLoadingEjecutar(true);
    try {
      const res = await pythonApiClient.post(
        "/procesos/hoteleria/cambio-habitacion/ejecutar",
        {
          asignacion_id: origen.asignacion_id,
          cama_destino_id: parseInt(camaDestinoId, 10),
          motivo: motivo || null,
          observaciones: observaciones || null,
        },
        { headers: headersProyecto(proyectoId) }
      );
      toast.success(res.data?.message || "Mudanza realizada");
      setShowConfirm(false);
      setOrigen(res.data?.destino || null);
      setPreview(null);
      setCamaDestinoId("");
      setCamasLibres([]);
      setMotivo("");
      setObservaciones("");
      cargarHistorial();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al ejecutar mudanza");
    } finally {
      setLoadingEjecutar(false);
    }
  };

  const listaModulos = Array.isArray(modulos) ? modulos : [];
  const listaHab = Array.isArray(habitaciones) ? habitaciones : [];

  return (
    <div className="dh-reporte-bg">
      <style>{`
        .dh-reporte-bg {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          min-height: 100vh; padding-bottom: 2rem;
        }
        .dh-main-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white; padding: 2rem 0; margin-bottom: 2rem;
          border-radius: 0 0 30px 30px;
        }
        .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
        .dh-main-subtitle { opacity: 0.9; margin-top: 0.5rem; }
        .dh-card { border: none; border-radius: 20px; box-shadow: 0 15px 35px rgba(0,0,0,0.1); }
        .dh-card-header {
          border-radius: 20px 20px 0 0; padding: 1.25rem 1.5rem; border: none;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white; font-weight: 700;
        }
        .btn-dh-primary {
          background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
          border: none; color: white; border-radius: 12px; padding: 10px 18px; font-weight: 600;
        }
        .btn-dh-secondary {
          background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
          border: none; color: white; border-radius: 12px; padding: 10px 18px; font-weight: 600;
        }
        .dh-form-control { border-radius: 10px; }
        .dh-alert-info {
          background: linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%);
          border: 1px solid #93c5fd; border-radius: 12px;
          padding: 1rem 1.25rem; color: #1e40af;
        }
        .info-box {
          background: #f9fafb; border-radius: 12px; padding: 1rem 1.25rem;
          border: 1px solid #e5e7eb;
        }
      `}</style>

      <div className="dh-main-header">
        <Container>
          <h1 className="dh-main-title">
            <FaExchangeAlt className="me-2" />
            Cambio de Habitación
          </h1>
          <p className="dh-main-subtitle">
            Mudanza interna sin reiniciar el ciclo de housekeeping (sábanas /
            habilitado)
          </p>
        </Container>
      </div>

      <Container>
        <Alert className="dh-alert dh-alert-info mb-4">
          Se conserva <code>fecha_inicio_ciclo_hk</code>. La cama origen queda
          liberada y aparece como <strong>Habilitado Completo</strong> en el
          plan de housekeeping del día.
        </Alert>

        <Row className="g-4">
          <Col lg={5}>
            <Card className="dh-card mb-4">
              <Card.Header className="dh-card-header">1. Huésped origen</Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">DNI</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      className="dh-form-control"
                      value={dni}
                      onChange={(e) => setDni(e.target.value)}
                      placeholder="Documento de identidad"
                      onKeyDown={(e) => e.key === "Enter" && buscar()}
                    />
                    <Button
                      className="btn-dh-primary"
                      onClick={buscar}
                      disabled={loadingBuscar}
                    >
                      {loadingBuscar ? (
                        <Spinner size="sm" />
                      ) : (
                        <FaSearch />
                      )}
                    </Button>
                  </div>
                </Form.Group>

                {origen && (
                  <div className="info-box">
                    <div className="fw-bold mb-1">{origen.trabajador}</div>
                    <div className="text-muted small mb-2">DNI {origen.dni}</div>
                    <div>
                      <Badge bg="primary" className="me-1">
                        {origen.zona}
                      </Badge>
                      <Badge bg="secondary" className="me-1">
                        {origen.modulo}
                      </Badge>
                      <Badge bg="info">{origen.hab_cama}</Badge>
                    </div>
                    <hr />
                    <div className="small">
                      Check-in local: <strong>{origen.fecha_check_in || "—"}</strong>
                      <br />
                      Ancla ciclo HK:{" "}
                      <strong>{origen.fecha_inicio_ciclo_hk || "—"}</strong>
                      <br />
                      Día del ciclo:{" "}
                      <Badge bg="warning" text="dark">
                        {origen.dias_ciclo}
                      </Badge>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={7}>
            <Card className="dh-card mb-4">
              <Card.Header className="dh-card-header">2. Cama destino</Card.Header>
              <Card.Body>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label className="fw-bold">Zona</Form.Label>
                    <Form.Select
                      className="dh-form-control"
                      value={zonaId}
                      disabled={!origen}
                      onChange={(e) => onZonaChange(e.target.value)}
                    >
                      <option value="">Seleccione...</option>
                      {(zonas || []).map((z) => (
                        <option key={z.id} value={z.id}>
                          {z.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className="fw-bold">Módulo</Form.Label>
                    <Form.Select
                      className="dh-form-control"
                      value={moduloId}
                      disabled={!zonaId}
                      onChange={(e) => onModuloChange(e.target.value)}
                    >
                      <option value="">Seleccione...</option>
                      {listaModulos.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.nombre}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className="fw-bold">Habitación</Form.Label>
                    <Form.Select
                      className="dh-form-control"
                      value={habitacionId}
                      disabled={!moduloId}
                      onChange={(e) => onHabitacionChange(e.target.value)}
                    >
                      <option value="">Seleccione...</option>
                      {listaHab.map((h) => (
                        <option key={h.id} value={h.id}>
                          {h.numero || h.codigo || h.id}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className="fw-bold">Cama libre</Form.Label>
                    <Form.Select
                      className="dh-form-control"
                      value={camaDestinoId}
                      disabled={!habitacionId}
                      onChange={(e) => {
                        setCamaDestinoId(e.target.value);
                        setPreview(null);
                      }}
                    >
                      <option value="">Seleccione...</option>
                      {camasLibres.map((c) => (
                        <option key={c.cama_id} value={c.cama_id}>
                          {c.codigo_cama}
                        </option>
                      ))}
                    </Form.Select>
                    {habitacionId && camasLibres.length === 0 && (
                      <Form.Text className="text-danger">
                        No hay camas libres en esta habitación
                      </Form.Text>
                    )}
                  </Col>
                  <Col md={12}>
                    <Form.Label className="fw-bold">Motivo (opcional)</Form.Label>
                    <Form.Control
                      className="dh-form-control"
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                      placeholder="Ej: Preferencia, mantenimiento, reorganización..."
                      disabled={!origen}
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label className="fw-bold">Observaciones</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      className="dh-form-control"
                      value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)}
                      disabled={!origen}
                    />
                  </Col>
                  <Col md={12}>
                    <Button
                      className="btn-dh-primary"
                      onClick={hacerPreview}
                      disabled={!origen || !camaDestinoId || loadingPreview}
                    >
                      {loadingPreview ? (
                        <Spinner size="sm" className="me-2" />
                      ) : (
                        <FaCheck className="me-2" />
                      )}
                      Vista previa y confirmar
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="dh-card mb-4">
          <Card.Header className="dh-card-header">
            <FaHistory className="me-2" />
            Historial reciente
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>DNI</th>
                    <th>Trabajador</th>
                    <th>Origen</th>
                    <th>Destino</th>
                    <th>Día ciclo</th>
                    <th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingHist ? (
                    <tr>
                      <td colSpan={7} className="text-center py-3">
                        <Spinner size="sm" />
                      </td>
                    </tr>
                  ) : historial.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-3">
                        Sin mudanzas registradas
                      </td>
                    </tr>
                  ) : (
                    historial.map((h) => (
                      <tr key={h.operacion_id}>
                        <td>{String(h.created_at || "").slice(0, 19)}</td>
                        <td>{h.dni}</td>
                        <td>{h.trabajador}</td>
                        <td>
                          {h.modulo_origen} / {h.habitacion_origen}-{h.cama_origen}
                        </td>
                        <td>
                          {h.modulo_destino} / {h.habitacion_destino}-
                          {h.cama_destino}
                        </td>
                        <td className="text-center">{h.dias_ciclo_al_trasladar}</td>
                        <td>{h.motivo || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Container>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar cambio de habitación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {preview && (
            <>
              <Alert variant="info">{preview.mensaje}</Alert>
              <p className="mb-1">
                <strong>Origen:</strong> {preview.origen?.hab_cama} (
                {preview.origen?.modulo})
              </p>
              <p className="mb-1">
                <strong>Destino:</strong> {preview.destino?.hab_cama} (
                {preview.destino?.modulo})
              </p>
              <p className="mb-0">
                <strong>Impacto HK:</strong>{" "}
                {preview.impacto_hk?.cama_origen}
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn-dh-secondary"
            onClick={() => setShowConfirm(false)}
            disabled={loadingEjecutar}
          >
            Cancelar
          </Button>
          <Button
            className="btn-dh-primary"
            onClick={confirmar}
            disabled={loadingEjecutar}
          >
            {loadingEjecutar ? (
              <Spinner size="sm" className="me-2" />
            ) : (
              <FaExchangeAlt className="me-2" />
            )}
            Confirmar mudanza
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" autoClose={3500} />
    </div>
  );
};

export default CambioHabitacion;
